import { FolderIcon } from '@carboncopyco/icons';
import type { DirectoryEntity, Folder, Workspace } from '@carboncopyco/types';
import { ColorHex } from '@carboncopyco/ui';
import * as Accordion from '@radix-ui/react-accordion';
import { useMutationObserver, useTrackedEffect } from 'ahooks';
import { useRef } from 'react';
import { TreeNode, encodeNodeId } from '../BrowseTree';
import { WorkspaceAvatar } from '../Workspaces';
import { AddNewFolderInput } from './AddNewFolderInput';
import { FolderTreeRow } from './FolderTreeRow';
import { FolderTreeSkeleton } from './FolderTreeSkeleton';
import type { Origin } from './types';

type FolderTreeProps = {
  workspaces: Workspace[];
  folders: Folder[];
  isLoading: boolean;
  selectedItemId: string;
  onSelectedItemId: (selectedItem: string) => void;
  openItemId: string;
  onOpenItemId: (selectedItem: string) => void;
  showAddNewFolderInput: boolean;
  closeAddNewFolderInput: () => void;
  actionOriginLocation: Origin;
  blockOtherWorkspaces?: boolean;
  tooltipText?: string;
};

export const FolderTree = ({
  isLoading,
  workspaces,
  folders,
  selectedItemId,
  onSelectedItemId,
  openItemId,
  onOpenItemId,
  showAddNewFolderInput,
  closeAddNewFolderInput,
  actionOriginLocation,
  blockOtherWorkspaces,
  tooltipText,
}: FolderTreeProps) => {
  const treeRootRef = useRef<HTMLDivElement>(null);

  const scrollToOriginMutationCallback = (mutationList, observer) => {
    for (const mutation of mutationList) {
      if (mutation.type === 'childList') {
        const scrollTarget = document.querySelector(
          "[class*='origin-tree-node']"
        );
        if (scrollTarget) {
          scrollTarget.scrollIntoView({
            behavior: 'smooth',
          });
          observer.disconnect();
          break;
        }
      }
    }
  };

  useMutationObserver(scrollToOriginMutationCallback, treeRootRef, {
    childList: true,
    subtree: true,
  });

  useTrackedEffect(
    (_, previousDeps) => {
      const isFirstRenderWithFolders =
        workspaces && folders && !previousDeps?.[0];
      if (isFirstRenderWithFolders) {
        onSelectedItemId(
          encodeNodeId(
            actionOriginLocation.id,
            actionOriginLocation.entity,
            actionOriginLocation.entity === 'workspace' ? 0 : 1,
            actionOriginLocation.workspaceId
          )
        );
        onOpenItemId(
          encodeNodeId('0', 'workspace', 0, actionOriginLocation.workspaceId)
        );
      }
    },
    [folders, workspaces, actionOriginLocation]
  );

  if (isLoading) {
    return <FolderTreeSkeleton />;
  }

  return (
    <Accordion.Root
      type="single"
      value={openItemId}
      onValueChange={onOpenItemId}
      collapsible
      className="flex flex-col"
    >
      <div
        ref={treeRootRef}
        className="flex flex-col gap-1"
      >
        {workspaces?.map((workspace) => {
          const workspaceNodeId = encodeNodeId<DirectoryEntity>(
            '0',
            'workspace',
            0,
            workspace.id
          );
          const isSelected = selectedItemId === workspaceNodeId;
          const isOpen = openItemId === workspaceNodeId;
          const isOrigin =
            actionOriginLocation.entity === 'workspace' &&
            actionOriginLocation.workspaceId === workspace.id;
          const currentWorkspaceFolders = folders.filter(
            (folder) => folder.workspaceId === workspace.id
          );

          function shouldDisableItem() {
            const { entity, id, workspaceId } = actionOriginLocation;
            if (entity === 'folder' && folders) {
              return String(workspace.id) !== String(workspaceId);
            }
            return id !== workspace.id && !isOrigin;
          }

          return (
            <div key={workspaceNodeId}>
              <TreeNode
                isOrigin={isOrigin}
                id={workspaceNodeId}
                isSelected={isSelected}
                onSelect={(id) => {
                  if (showAddNewFolderInput) {
                    return;
                  }
                  closeAddNewFolderInput();
                  onSelectedItemId(id);
                }}
                disabled={blockOtherWorkspaces && shouldDisableItem()}
                header={
                  <FolderTreeRow
                    hasContent={!!currentWorkspaceFolders.length}
                    isOpen={workspaceNodeId === openItemId}
                    name={workspace.workspaceName}
                    icon={
                      <WorkspaceAvatar
                        workspace={workspace}
                        size="small"
                      />
                    }
                    isOrigin={isOrigin}
                  />
                }
                content={
                  <div className="flex flex-col gap-1">
                    {showAddNewFolderInput && isOpen && (
                      <AddNewFolderInput
                        onClose={closeAddNewFolderInput}
                        selected={selectedItemId}
                        setSelected={onSelectedItemId}
                      />
                    )}
                    {currentWorkspaceFolders.map((folder) => {
                      const isOrigin =
                        actionOriginLocation.entity === 'folder' &&
                        actionOriginLocation.id === folder.id;

                      const folderNodeId = encodeNodeId<DirectoryEntity>(
                        folder.id,
                        'folder',
                        1,
                        folder.workspaceId
                      );
                      return (
                        <TreeNode<DirectoryEntity>
                          key={folderNodeId}
                          isOrigin={isOrigin}
                          id={folderNodeId}
                          header={
                            <FolderTreeRow
                              name={folder.name}
                              icon={<FolderIcon color={ColorHex.natural500} />}
                              isOrigin={isOrigin}
                            />
                          }
                          isSelected={folderNodeId === selectedItemId}
                          onSelect={(id) => {
                            if (showAddNewFolderInput) {
                              return;
                            }
                            onSelectedItemId(id);
                          }}
                        />
                      );
                    })}
                  </div>
                }
                tooltipText={tooltipText}
              />
            </div>
          );
        })}
      </div>
    </Accordion.Root>
  );
};
